import type { components } from '@fluencelabs/vodopad-admin-api'
import { useCustom } from '@refinedev/core'
import { useMemo } from 'react'

import { VODOPAD_ENDPOINT } from '@/constants/constants'
import { useVmConfigurations } from '@/hooks/useVmConfigurations'
import { ensureArray } from '@/types/api'

import type { PriceEntry, PriceResourceType } from './priceUtils'
import { formatVmConfig, PRICE_SCHEDULE_URLS, toPriceEntry } from './priceUtils'

function matchEntries(
  data: unknown[],
  resourceType: PriceResourceType,
  comboKey: string,
  configurations: components['schemas']['VmConfigurationDto'][],
): { entries: PriceEntry[]; clusterId: string; label: string } {
  const empty = { entries: [], clusterId: '', label: '' }

  for (const item of data) {
    const rec = item as Record<string, unknown>
    const schedule = rec.priceSchedule as { id: string }[] | undefined
    if (!schedule) continue

    if (resourceType === 'vm') {
      const tid = rec.vmTypeId as {
        clusterId: string
        vmConfigurationId: string
      }
      if (`${tid.clusterId}:${tid.vmConfigurationId}` === comboKey) {
        const cfg = configurations.find((c) => c.id === tid.vmConfigurationId)
        return {
          entries: (
            schedule as unknown as Parameters<typeof toPriceEntry>[0][]
          ).map(toPriceEntry),
          clusterId: tid.clusterId,
          label: cfg ? formatVmConfig(cfg) : tid.vmConfigurationId,
        }
      }
    } else if (resourceType === 'storage') {
      const tid = rec.storageTypeId as {
        clusterId: string
        storageType: string
        replicated: boolean
      }
      if (
        `${tid.clusterId}:${tid.storageType}:${tid.replicated}` === comboKey
      ) {
        return {
          entries: (
            schedule as unknown as Parameters<typeof toPriceEntry>[0][]
          ).map(toPriceEntry),
          clusterId: tid.clusterId,
          label: `${tid.storageType}${tid.replicated ? ' (replicated)' : ''}`,
        }
      }
    } else {
      const tid = rec.publicIpTypeId as {
        clusterId: string
        addressType: string
      }
      if (`${tid.clusterId}:${tid.addressType}` === comboKey) {
        return {
          entries: (
            schedule as unknown as Parameters<typeof toPriceEntry>[0][]
          ).map(toPriceEntry),
          clusterId: tid.clusterId,
          label: tid.addressType === 'V4' ? 'IPv4' : 'IPv6',
        }
      }
    }
  }
  return empty
}

export function useScheduleEntries(
  groupName: string,
  resourceType: PriceResourceType,
  comboKey: string,
) {
  const query = useCustom<unknown[]>({
    url: `${VODOPAD_ENDPOINT}${PRICE_SCHEDULE_URLS[resourceType]}`,
    method: 'get',
    config: { query: { userGroup: groupName } },
    queryOptions: { staleTime: 60_000 },
  })

  const { configurations } = useVmConfigurations()

  const result = useMemo(
    () =>
      matchEntries(
        ensureArray<unknown>(query.query.data?.data),
        resourceType,
        comboKey,
        configurations,
      ),
    [query.query.data?.data, resourceType, comboKey, configurations],
  )

  return {
    ...result,
    loading: query.query.isLoading,
    refetch: () => void query.query.refetch(),
  }
}
